import {computed, Injectable, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {HttpBackend, HttpClient} from '@angular/common/http';
import {catchError, Observable, of, switchMap, tap} from 'rxjs';
import * as moment from 'moment';
import 'moment/min/locales'; // All locales bundled — no code changes needed when adding a language

export interface Language {
    code: string;
    flag: string;
    bcp47: string;
}

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private readonly COOKIE_NAME = 'app_language';
    private readonly DEFAULT_LANGUAGE = 'en';

    /** Populated from i18n/languages.json before the app renders. */
    public readonly availableLanguages = signal<Language[]>([]);

    public readonly currentLang = signal<string>(this.DEFAULT_LANGUAGE);

    /** BCP 47 locale tag for the active language — used by ion-datetime. */
    public readonly currentBcp47 = computed(() =>
        this.availableLanguages().find(l => l.code === this.currentLang())?.bcp47 ?? 'nl-NL'
    );

    // Use HttpBackend directly so the languages.json request bypasses all
    // API interceptors (auth, unwrap, error) — it's a plain static asset.
    private readonly http: HttpClient;

    constructor(private translateService: TranslateService, httpBackend: HttpBackend) {
        this.http = new HttpClient(httpBackend);
        const saved = this.getCookie(this.COOKIE_NAME);
        // Apply saved language immediately; validity is confirmed against the
        // manifest once init() completes.
        if (saved) {
            this.currentLang.set(saved);
            moment.locale(saved);
        }
    }

    /**
     * Called by APP_INITIALIZER:
     * 1. Loads i18n/languages.json to discover available languages.
     * 2. Falls back to the default if the cookie held an unknown code.
     * 3. Loads the active translation file.
     */
    public init(): Observable<unknown> {
        return this.http.get<Language[]>('./i18n/languages.json').pipe(
            tap(languages => {
                this.availableLanguages.set(languages);
                if (!this.isValid(this.currentLang())) {
                    this.currentLang.set(this.DEFAULT_LANGUAGE);
                    moment.locale(this.DEFAULT_LANGUAGE);
                }
            }),
            catchError(() => {
                // If languages.json fails (e.g. network or path issue), fall back to
                // a hardcoded list so APP_INITIALIZER never blocks the app from loading.
                console.warn('LanguageService: could not load languages.json, falling back to defaults.');
                this.availableLanguages.set([
                    {code: 'nl', flag: '🇳🇱', bcp47: 'nl-NL'},
                    {code: 'en', flag: '🇬🇧', bcp47: 'en-US'},
                    {code: 'fr', flag: '🇫🇷', bcp47: 'fr-FR'},
                    {code: 'de', flag: '🇩🇪', bcp47: 'de-DE'},
                    {code: 'es', flag: '🇪🇸', bcp47: 'es-ES'},
                    {code: 'pl', flag: '🇵🇱', bcp47: 'pl-PL'},
                ]);
                return of([]);
            }),
            switchMap(() => this.translateService.use(this.currentLang()))
        );
    }

    public setLanguage(lang: string): void {
        if (!this.isValid(lang)) return;
        // moment.locale must be set BEFORE translateService.use() because once
        // a language file is cached, use() emits onLangChange synchronously —
        // any subscribers (e.g. chart re-render) must already see the new locale.
        moment.locale(lang);
        this.currentLang.set(lang);
        this.setCookie(this.COOKIE_NAME, lang, 365);
        this.translateService.use(lang);
    }

    private isValid(lang: string): boolean {
        return this.availableLanguages().some(l => l.code === lang);
    }

    private setCookie(name: string, value: string, days: number): void {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
    }

    private getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? match[2] : null;
    }
}
